import { useDSDFiles } from "@/hooks/use-dsd-files";
import { DsdFileStatus } from "@/types/dsd";
import { checkVersionForward } from "@/apis/dsd/route";
import { FileDropArea } from "@/components/file-drop-zone";
import { ModalWrapper } from "@/components/modals/modal-wrapper";
import { NewFileCard } from "@/components/modals/version-update/new-file-card";
import {
  asReportPeriod,
  asReportType,
} from "@/components/team-workspace/report-list/group-reports";
import { popModal, pushModal } from "@/stores/modal";
import { Button } from "@/toggle-ui-kit/button";
import { getFormattedTimeWithDate } from "@/utils/time";
import * as Sentry from "@sentry/react";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { parseDsdFilename, ParsedDsdMeta } from "./parse-dsd-filename";
import {
  ExistingReportSummary,
  SamePeriodComparison,
  SamePeriodSection,
  UploadMode,
} from "./same-period-section";
import { pushUploadingModal } from "./uploading-phase";

interface UploadDsdModalProps {
  /** 외부(드래그 등)에서 파일을 미리 전달받은 경우 바로 요약 단계로 시작 */
  file?: File;
}

const DSD_EXTENSION = ".dsd";

const formatDate = (iso: string) =>
  getFormattedTimeWithDate(iso).replace(/\s/g, "").replace(/\.$/, "");

// ============================================
// UploadDsdModal
// 파일 선택 단계(드롭존 → 파일 요약/동일기간 비교)까지만 담당한다. 업로드를 확정하면
// 진행률 화면(UploadingPhase)을 별도 모달로 띄우고 이 모달은 닫힌다 — 선택 단계는 ESC 로
// 닫아도 되지만 업로드 중에는 닫히면 안 되므로, 닫힘 규칙이 다른 두 화면을 한 인스턴스에
// 겸업시키지 않는다.
// 동일 기간 보고서 존재 여부는 백엔드 version-forward/check(파일 내용 기반)로 판단한다.
// ============================================

export const UploadDsdModal = ({ file: initialFile }: UploadDsdModalProps) => {
  // 후보의 유형/기간 뱃지를 채우기 위해 기존 목록을 참조
  const { data: dsdFiles } = useDSDFiles({
    status: DsdFileStatus.DSD_ANALYZED,
  });

  const [file, setFile] = useState<File | null>(initialFile ?? null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<UploadMode>("version");

  // 동일 기간 후보 조회 (백엔드, 파일 내용 기반)
  const {
    mutate: runCheck,
    data: checkData,
    isPending: isChecking,
  } = useMutation({
    mutationFn: (f: File) => checkVersionForward(f),
    // 화면 되돌리기는 mutate 쪽 onError 가 맡는다 — 여기 콜백은 뒤늦게 끝난 옛 요청에도 불려서
    // 이미 교체한 새 파일을 지워버린다. 리포팅은 그 경우에도 남겨야 하니 이 자리에 둔다.
    // (버전 업데이트 모달과 같은 규칙)
    onError: (error, f) =>
      Sentry.captureException(error, {
        extra: { filename: f.name, fileSize: f.size },
      }),
  });

  // 새 파일 메타(뱃지): 백엔드가 파일 내용으로 감지한 detected 우선, 없으면 파일명 추정 폴백
  const meta = useMemo<ParsedDsdMeta | null>(() => {
    const detected = checkData?.detected;
    if (detected) {
      return {
        year: detected.reportYear,
        type:
          detected.financialStatementType === "BOTH"
            ? undefined
            : detected.financialStatementType,
        period: detected.reportQuarter,
      };
    }
    return file ? parseDsdFilename(file.name) : null;
  }, [checkData, file]);

  const samePeriod = useMemo<ExistingReportSummary | null>(() => {
    const candidate = checkData?.candidates?.[0];
    if (!candidate) return null;
    const existing = dsdFiles?.find((f) => f.id === candidate.workbookId);
    return {
      id: candidate.workbookId,
      name: candidate.displayName ?? candidate.name,
      type: existing
        ? asReportType(existing.financialStatementType)
        : undefined,
      year: existing?.reportYear ?? undefined,
      period: existing ? asReportPeriod(existing.reportQuarter) : undefined,
      uploadDate: formatDate(candidate.createdAt),
      currentVersion: existing ? `V${existing.versionNo}` : undefined,
    };
  }, [checkData, dsdFiles]);

  // 경고 배너·라디오(SamePeriodSection)와 비교 카드(SamePeriodComparison)는 같은 조건에서
  // 함께 나와야 한다 — "비교 카드는 있는데 라디오가 없는" 상태가 생기지 않도록 판정은 여기 한 곳.
  const showSamePeriod = !!file && !isChecking && !!samePeriod && !!meta;

  const handleFile = (selected: File) => {
    setError(null);
    setFile(selected);
    // 파일이 바뀌면 업로드 방식 선택도 처음부터 다시 — 새 파일에 동일 기간 보고서가
    // 있는지부터 다시 판단한다.
    setMode("version");
    // 확인 중에도 영역이 열려 있어 파일을 교체할 수 있다. mutate 에 넘긴 콜백은 마지막 요청에만
    // 불리므로, 앞선 요청이 뒤늦게 실패해도 방금 고른 파일을 지우지 않는다.
    // check 실패는 백엔드가 이 파일을 열지 못했다는 뜻 — 그대로 진행시키면 파일명 추정
    // 뱃지로 정상인 척 보이다가 60초짜리 업로드 끝에서야 터진다. 여기서 끊는다.
    runCheck(selected, {
      onError: () => {
        setFile(null);
        setError(
          "이 파일을 읽을 수 없습니다. DSD 파일이 맞는지 확인 후 다시 업로드해 주세요.",
        );
      },
    });
  };

  const removeFile = () => {
    setFile(null);
    setError(null);
  };

  const startUpload = () => {
    if (!file) return;
    // 동일 기간 보고서를 "최신 버전으로" 선택했을 때만 버전 포워드로 이어붙인다.
    const srcWorkbookId =
      samePeriod && mode === "version" ? samePeriod.id : null;
    popModal();
    pushUploadingModal(file, srcWorkbookId);
  };

  const renderSelected = () => {
    if (!file) return null;
    if (isChecking) {
      return (
        <div className="flex h-[72px] items-center justify-center gap-2 caption-1-medium text-label-alternative">
          <Loader2 className="size-5 animate-spin" />
          동일 기간 보고서 확인 중...
        </div>
      );
    }
    if (showSamePeriod) {
      return (
        <SamePeriodComparison
          newFilename={file.name}
          newMeta={meta}
          existing={samePeriod}
          onRemove={removeFile}
        />
      );
    }
    return (
      meta && (
        <NewFileCard filename={file.name} meta={meta} onRemove={removeFile} />
      )
    );
  };

  const dropArea = (
    <FileDropArea
      accept={DSD_EXTENSION}
      hint="*.dsd 형식"
      className="h-auto flex-1"
      compactWhenSelected
      error={error}
      selectedHint={
        file ? "다른 파일로 교체하려면 클릭하거나 드래그하세요" : undefined
      }
      onFilesSelect={(files) => handleFile(files[0])}
      onReject={({ message }) => {
        // 오류 상태는 '파일 없음 + 오류' 하나로 둔다 — 앞서 고른 파일 카드가 남아 있으면
        // 업로드해도 되는지 헷갈린다. (버전 업데이트 모달과 같은 규칙)
        setFile(null);
        setError(message);
      }}
    >
      {file && (
        <div className="flex flex-col gap-2">
          <div className="h-px w-full bg-line-normal" />
          {renderSelected()}
        </div>
      )}
    </FileDropArea>
  );

  return (
    <ModalWrapper className="flex h-[550px] w-[600px] max-w-[600px] flex-col gap-0 rounded-lg border border-line-normal bg-white p-0 shadow-xxlarge">
      <div className="flex items-center px-6 pt-5">
        <h2 className="headline-1-semibold text-label-normal">
          DSD 보고서 업로드
        </h2>
      </div>
      <div className="px-6 pb-3 pt-2">
        <p className="label-1-normal-regular text-label-alternative">
          DSD 보고서를 업로드해 주세요. 동일 기간의 보고서는 최신 버전으로
          업로드할 수 있습니다.
        </p>
      </div>

      <div className="flex flex-1 flex-col gap-2 overflow-y-auto px-6 pb-6 pt-4">
        {showSamePeriod ? (
          <SamePeriodSection mode={mode} onModeChange={setMode}>
            {dropArea}
          </SamePeriodSection>
        ) : (
          dropArea
        )}
      </div>

      <div className="flex items-center justify-end gap-2 px-6 pb-5">
        <Button variant="outlined" size="base" onClick={popModal}>
          취소
        </Button>
        <Button
          variant="solid"
          color="primary"
          size="base"
          disabled={!file || isChecking}
          onClick={startUpload}
        >
          보고서 업로드
        </Button>
      </div>
    </ModalWrapper>
  );
};

/**
 * DSD 업로드 모달을 띄운다. 홈의 진입점이 둘(PageHeader 버튼 / 빈 상태 버튼)이라
 * 열기 규칙을 여기 한 곳에 둔다 — 각자 push 하면 "헤더로 열면 ESC 되는데 빈 상태
 * 버튼으로 열면 안 됨" 같은 조용한 불일치가 생긴다.
 *
 * dim 클릭으로는 닫지 않는다(고른 파일이 날아가는 오조작 방지). 의도가 분명한 ESC 는 허용.
 */
export const openUploadDsdModal = () => {
  pushModal(UploadDsdModal, { closeOnPointerDownOutside: false });
};
